import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import type { MemberLevel } from '@/types/admin';

const MemberLevels = () => {
  const [levels, setLevels] = useState<MemberLevel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    level: 0,
    name: '',
    min_points: 0,
    max_points: 0,
    color: 'bg-gray-500',
    benefits: ['']
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    try {
      const { data, error } = await supabase
        .from('member_levels')
        .select('*')
        .order('level');

      if (error) throw error;
      
      // Convert Json benefits to string array
      const processedData = (data || []).map(level => ({
        ...level,
        benefits: Array.isArray(level.benefits) 
          ? level.benefits as string[]
          : JSON.parse(level.benefits as string || '[]') as string[]
      })) as MemberLevel[];
      
      setLevels(processedData);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: '載入失敗',
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const benefitsArray = formData.benefits.filter(b => b.trim() !== '');
      
      if (editingId && editingId !== 'new') {
        const { error } = await supabase
          .from('member_levels')
          .update({
            ...formData,
            benefits: JSON.stringify(benefitsArray)
          })
          .eq('id', editingId);

        if (error) throw error;
        toast({ title: '更新成功', description: '會員等級已更新' });
      } else {
        const { error } = await supabase
          .from('member_levels')
          .insert({
            ...formData,
            benefits: JSON.stringify(benefitsArray)
          });

        if (error) throw error;
        toast({ title: '新增成功', description: '會員等級已新增' });
      }

      setEditingId(null);
      resetForm();
      fetchLevels();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: '操作失敗',
        description: error.message,
      });
    }
  };

  const handleEdit = (level: MemberLevel) => {
    setEditingId(level.id);
    setFormData({
      level: level.level,
      name: level.name,
      min_points: level.min_points,
      max_points: level.max_points,
      color: level.color,
      benefits: level.benefits
    });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('確定要刪除此會員等級嗎？')) return;

    try {
      const { error } = await supabase
        .from('member_levels')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({ title: '刪除成功', description: '會員等級已刪除' });
      fetchLevels();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: '刪除失敗',
        description: error.message,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      level: 0,
      name: '',
      min_points: 0,
      max_points: 0,
      color: 'bg-gray-500',
      benefits: ['']
    });
  };

  const addBenefit = () => {
    setFormData(prev => ({
      ...prev,
      benefits: [...prev.benefits, '']
    }));
  };

  const updateBenefit = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.map((b, i) => i === index ? value : b)
    }));
  };

  const removeBenefit = (index: number) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">會員等級管理</h1>
        <Button onClick={() => setEditingId('new')}>
          <Plus className="w-4 h-4 mr-2" />
          新增等級
        </Button>
      </div>

      {editingId && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId === 'new' ? '新增會員等級' : '編輯會員等級'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">等級</label>
                <Input
                  type="number"
                  value={formData.level}
                  onChange={(e) => setFormData(prev => ({ ...prev, level: parseInt(e.target.value) }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">名稱</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">最低積分</label>
                <Input
                  type="number"
                  value={formData.min_points}
                  onChange={(e) => setFormData(prev => ({ ...prev, min_points: parseInt(e.target.value) }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">最高積分</label>
                <Input
                  type="number"
                  value={formData.max_points}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_points: parseInt(e.target.value) }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">顏色樣式</label>
                <select
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-full p-2 border rounded"
                >
                  <option value="bg-gray-500">灰色</option>
                  <option value="bg-blue-500">藍色</option>
                  <option value="bg-purple-500">紫色</option>
                  <option value="bg-orange-500">橙色</option>
                  <option value="bg-yellow-500">黃色</option>
                  <option value="bg-green-500">綠色</option>
                  <option value="bg-red-500">紅色</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">等級權益</label>
              {formData.benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <Input
                    value={benefit}
                    onChange={(e) => updateBenefit(index, e.target.value)}
                    placeholder="輸入權益內容"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => removeBenefit(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={addBenefit}>
                <Plus className="w-4 h-4 mr-2" />
                新增權益
              </Button>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                儲存
              </Button>
              <Button variant="outline" onClick={() => {
                setEditingId(null);
                resetForm();
              }}>
                取消
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {levels.map((level) => (
          <Card key={level.id}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2">
                    <Badge className={`${level.color} text-white`}>
                      Lv.{level.level}
                    </Badge>
                    <h3 className="text-lg font-semibold">{level.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    積分範圍：{level.min_points} - {level.max_points}
                  </p>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">權益：</p>
                    <ul className="text-sm text-gray-600 list-disc list-inside">
                      {level.benefits.map((benefit: string, index: number) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(level)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(level.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default MemberLevels;
